/** Job/File 표시 관련 헬퍼. */

import { useTranslation } from "react-i18next";

/** ISO 문자열 → "YYYY-MM-DD HH:mm" (로컬 타임존). */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

/**
 * 경과 시간 표시 hook — i18n 대응.
 *
 * 반환값 ``relativeOf(iso)`` 는 다음과 같이 분기:
 *   - null/invalid → "—"
 *   - 5초 미만 → ``time.justNow``
 *   - 1분 미만 → ``time.secondsAgo`` (count=초)
 *   - 1시간 미만 → ``time.minutesAgo`` (count=분)
 *   - 1일 미만 → ``time.hoursAgo`` (count=시)
 *   - 그 외 → ``time.daysAgo`` (count=일)
 *
 * Hook 형태로 노출하는 이유:
 *   - 언어 변경 시 ``useTranslation`` 이 컴포넌트 re-render 를 트리거.
 *   - i18next 의 ``t`` 클로저를 caller 에 분산하지 않고 단일 진입점 제공.
 */
export function useRelativeTime() {
  const { t } = useTranslation();
  return (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const diff = Date.now() - d.getTime();
    const sec = Math.floor(diff / 1000);
    if (sec < 5) return t("time.justNow");
    if (sec < 60) return t("time.secondsAgo", { count: sec });
    const min = Math.floor(sec / 60);
    if (min < 60) return t("time.minutesAgo", { count: min });
    const hr = Math.floor(min / 60);
    if (hr < 24) return t("time.hoursAgo", { count: hr });
    const day = Math.floor(hr / 24);
    return t("time.daysAgo", { count: day });
  };
}

/** 처리 진행률 — pending/processing/completed 모두 0~100%. */
export function progressPercent(
  total: number,
  ok: number,
  rejected: number,
  errored: number,
): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round(((ok + rejected + errored) / total) * 100));
}
